import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A list screen's view state — what it is searched for, what it is filtered
 * by, which page is showing — kept in the URL instead of in component state.
 *
 * React Router unmounts a screen the moment the reader leaves it, so
 * anything held in useState is gone by the time they come back: the list
 * rebuilds itself from default filters and the search someone had typed is
 * simply lost. The URL survives that, because going Back IS the browser
 * restoring a URL. Put the view state there and the screen comes back the
 * way it was left, with no cache to keep in step and nothing to invalidate;
 * ScrollMemory then returns the reader to the row they were reading.
 *
 * `defaults` names the state, and the TYPE of each default says how to read
 * that param back out: a number is parsed, a boolean is 'true'/'false', an
 * object or an array is JSON, anything else is the raw string. A value equal
 * to its default is left out of the query string altogether, so an untouched
 * screen keeps a clean address and a shared link carries only what was
 * actually chosen.
 *
 * Params the caller has not named are never touched, which is the whole
 * point of `dropParams`. A one-shot command such as `?booking=<id>` from the
 * notification bell has to be obeyed once and then taken out of the URL, and
 * doing that with `setSearchParams({})` — as Orders used to — throws away
 * the durable view state sitting next to it.
 */

/** Read one param back in the shape its default declares. */
function decode(raw, fallback) {
  if (raw === null) return fallback;

  if (typeof fallback === 'number') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  if (typeof fallback === 'boolean') return raw === 'true';

  if (fallback !== null && typeof fallback === 'object') {
    try {
      return JSON.parse(raw);
    } catch (err) {
      // A URL someone truncated, hand-edited or pasted out of an email should
      // show the screen's default view, not a blank page with a stack trace.
      return fallback;
    }
  }

  return raw;
}

/**
 * How a value is spelled in the query string, or null when it says nothing —
 * an empty search, an empty filter set — and so does not belong in the URL.
 */
function encode(value) {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json === '{}' || json === '[]' ? null : json;
  }

  return String(value);
}

export default function useUrlViewState(defaults) {
  const [params, setSearchParams] = useSearchParams();

  // Both of these are read through refs so that `setView` and `dropParams`
  // never change identity. A debounced writer holds on to the callback it was
  // built with, and rebuilding it mid-keystroke would drop the keystrokes
  // still waiting in it.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const keys = Object.keys(defaults);

  // `view` must keep its identity unless one of ITS OWN params moved. The
  // fetch effect keys off it, and consuming a one-shot param rewrites the
  // query string without changing the view — that must not refetch the list.
  const signature = keys.map((key) => `${key}=${params.get(key) ?? ''}`).join('&');

  const view = useMemo(
    () => keys.reduce((state, key) => {
      const decoded = decode(paramsRef.current.get(key), defaultsRef.current[key]);
      return { ...state, [key]: decoded };
    }, {}),
    [signature],
  );

  /**
   * Merge a patch into the view. REPLACE by default: editing a filter is not
   * a place the reader has travelled to, and pushing one entry per edit would
   * make Back walk the whole way out of the screen.
   */
  const setView = useCallback((patch, { replace = true } = {}) => {
    setSearchParamsRef.current((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(patch).forEach(([key, value]) => {
        const encoded = encode(value);
        if (encoded === null || encoded === encode(defaultsRef.current[key])) next.delete(key);
        else next.set(key, encoded);
      });
      return next;
    }, { replace });
  }, []);

  /** Consume one-shot command params, leaving the durable view state alone. */
  const dropParams = useCallback((names) => {
    // Nothing to consume means no navigation at all: a REPLACE for its own
    // sake still re-renders the screen and would loop with the effect that
    // asked for it.
    if (!names.some((name) => paramsRef.current.has(name))) return;

    setSearchParamsRef.current((previous) => {
      const next = new URLSearchParams(previous);
      names.forEach((name) => next.delete(name));
      return next;
    }, { replace: true });
  }, []);

  return {
    view, setView, params, dropParams,
  };
}
